import React, { memo, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import './ProjectCard.scss'

// Status configuration - defined outside component to avoid recreation
const STATUS_CONFIG = {
	draft: { label: 'Draft', variant: 'default' },
	pending_pm_review: { label: 'PM Review', variant: 'warning' },
	pending_executive_review: { label: 'Exec Review', variant: 'info' },
	approved: { label: 'Approved', variant: 'success' },
	rejected: { label: 'Rejected', variant: 'danger' },
	active: { label: 'Active', variant: 'success' },
	completed: { label: 'Completed', variant: 'success' },
	on_hold: { label: 'On Hold', variant: 'warning' }
}

/**
 * ProjectCard - Project summary card for dashboard
 * Memoized for performance optimization.
 *
 * @param {Object} project - Project data
 * @param {Function} onClick - Click handler
 * @param {boolean} compact - Compact display mode
 * @param {boolean} loading - Show loading state
 */
const ProjectCard = memo(({ project, onClick, compact = false, loading = false }) => {
	// Memoize status configuration lookup
	const statusConfig = useMemo(() => {
		const status = project?.approval_status || project?.status
		return STATUS_CONFIG[status] || { label: status || 'Unknown', variant: 'default' }
	}, [project?.approval_status, project?.status])

	// Memoize progress calculation
	const progress = useMemo(() => {
		if (!project) return 0
		const { total_tasks = 0, completed_tasks = 0 } = project
		if (total_tasks === 0) return 0
		return Math.round((completed_tasks / total_tasks) * 100)
	}, [project])

	// Memoize class name
	const cardClass = useMemo(() =>
		`project-card ${compact ? 'project-card--compact' : ''} ${onClick ? 'project-card--clickable' : ''}`,
		[compact, onClick]
	)

	// Memoize keypress handler
	const handleKeyPress = useCallback((e) => {
		if (e.key === 'Enter' && onClick) {
			onClick()
		}
	}, [onClick])

	if (loading) {
		return (
			<div
				className={`project-card ${compact ? 'project-card--compact' : ''}`}
				aria-busy="true"
				aria-label="Loading project"
			>
				<div className="project-card__skeleton">
					<div className="skeleton skeleton--header" aria-hidden="true">
						<div className="skeleton skeleton--title"></div>
						<div className="skeleton skeleton--status"></div>
					</div>
					<div className="skeleton skeleton--meta" aria-hidden="true"></div>
					<div className="skeleton skeleton--progress" aria-hidden="true"></div>
				</div>
			</div>
		)
	}

	if (!project) return null

	return (
		<article
			className={cardClass}
			onClick={onClick}
			role={onClick ? 'button' : undefined}
			tabIndex={onClick ? 0 : undefined}
			onKeyPress={onClick ? handleKeyPress : undefined}
			aria-label={`Project: ${project.name}. Status: ${statusConfig.label}. Progress: ${progress}%`}
		>
			<div className="project-card__header">
				<h4 className="project-card__title">{project.name}</h4>
				<span className={`project-card__status project-card__status--${statusConfig.variant}`}>
					{statusConfig.label}
				</span>
			</div>

			{!compact && project.description && (
				<p className="project-card__description">{project.description}</p>
			)}

			<div className="project-card__meta">
				{project.framework_name && (
					<span className="project-card__framework">
						<svg viewBox="0 0 24 24" fill="currentColor">
							<path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
						</svg>
						{project.framework_name}
					</span>
				)}
				{project.due_date && (
					<span className="project-card__due-date">
						<svg viewBox="0 0 24 24" fill="currentColor">
							<path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
						</svg>
						{project.due_date}
					</span>
				)}
			</div>

			<div className="project-card__progress">
				<div className="project-card__progress-header">
					<span className="project-card__progress-label">Progress</span>
					<span className="project-card__progress-value">{progress}%</span>
				</div>
				<div className="project-card__progress-bar">
					<div
						className="project-card__progress-fill"
						style={{ width: `${progress}%` }}
					/>
				</div>
				{!compact && (
					<div className="project-card__progress-stats">
						<span>{project.completed_tasks || 0} / {project.total_tasks || 0} tasks</span>
					</div>
				)}
			</div>

			{!compact && project.team_members && project.team_members.length > 0 && (
				<div className="project-card__team">
					<div className="project-card__avatars">
						{project.team_members.slice(0, 3).map((member, index) => (
							<div
								key={member.id || index}
								className="project-card__avatar"
								title={member.name}
							>
								{member.avatar ? (
									<img src={member.avatar} alt={member.name} />
								) : (
									<span>{member.name?.charAt(0) || '?'}</span>
								)}
							</div>
						))}
						{project.team_members.length > 3 && (
							<div className="project-card__avatar project-card__avatar--more">
								+{project.team_members.length - 3}
							</div>
						)}
					</div>
				</div>
			)}
		</article>
	)
})

ProjectCard.displayName = 'ProjectCard'

ProjectCard.propTypes = {
	project: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		name: PropTypes.string.isRequired,
		description: PropTypes.string,
		status: PropTypes.string,
		approval_status: PropTypes.string,
		framework_name: PropTypes.string,
		due_date: PropTypes.string,
		total_tasks: PropTypes.number,
		completed_tasks: PropTypes.number,
		team_members: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				name: PropTypes.string,
				avatar: PropTypes.string
			})
		)
	}),
	onClick: PropTypes.func,
	compact: PropTypes.bool,
	loading: PropTypes.bool
}

export default ProjectCard
